import { useCallback, useEffect, useMemo, useState } from 'react';
import { DEFAULT_FILE_SORT, sortFiles, creatorTitleResolver, type FileSort } from '../../../lib/files-sort';
import { readFilesViewPref, writeFilesViewPref } from '../../../lib/files-view-pref';
import type { FilesViewMode } from '../../../lib/files-url-state';
import { useAgentName } from '../../../lib/use-agent-name';
import { useTranslation } from '../../../lib/i18n';
import { type OverflowMenuAction } from '../ui';
import { FilesTable } from './FilesTable';
import { FilesGrid } from './FilesGrid';
import { FilesViewToggle } from './FilesViewToggle';
import type { FilesRow } from './files-row';
import browserStyles from './FilesBrowser.module.css';
import styles from './FilesFlatList.module.css';

// The same Files list, over a set of files that has no tree: the view toggle, then the table rows or the
// thumbnail tiles `FilesBrowser` draws. No filter bar, no breadcrumb, no folder rows — a conversation's
// attachments have no paths, so there is nothing to browse into and nothing to narrow by Space or folder.
//
// This is what the per-chat Files panel renders, so a file looks the same there as it does on the Files page.
// Mirrors iOS/Android `FilesFlatBody`.

export function FilesFlatList({
  rows: unsorted,
  onOpen,
  onShare,
  actionsFor,
  onRowMenu,
  onBi,
  emptyCaption,
}: {
  rows: FilesRow[];
  onOpen: (row: FilesRow) => void;
  onShare: (row: FilesRow) => void;
  actionsFor: (row: FilesRow) => OverflowMenuAction[][];
  onRowMenu?: (row: FilesRow) => void;
  onBi?: (component: string) => void;
  emptyCaption: string;
}) {
  const { t } = useTranslation();
  const agent = useAgentName();
  const [sort, setSort] = useState<FileSort>(DEFAULT_FILE_SORT);
  // Rendered the same on the server for everyone; the remembered choice arrives after mount, as the browser
  // does it (`useFilesBrowser`), so a stored preference can never produce a hydration mismatch.
  const [view, setViewState] = useState<FilesViewMode>('list');
  useEffect(() => {
    const remembered = readFilesViewPref();
    if (remembered) setViewState(remembered);
  }, []);
  const setView = useCallback((next: FilesViewMode) => {
    setViewState(next);
    writeFilesViewPref(next);
  }, []);

  // Created-by sorts by the NAME you can read, not the raw actor enum behind it.
  const creatorTitle = useMemo(() => creatorTitleResolver(t, agent.short), [t, agent.short]);
  const rows = useMemo(() => {
    const items = unsorted.map((row) => row.item);
    const order = new Map(sortFiles(items, sort, creatorTitle).map((item, i) => [item, i]));
    return [...unsorted].sort((a, b) => (order.get(a.item) ?? 0) - (order.get(b.item) ?? 0));
  }, [unsorted, sort, creatorTitle]);

  // Data-gated columns, as on the Files page: a column shows only when at least one file can fill it.
  const showCreatedBy = rows.some((row) => row.item.builtBy);
  const showDate = rows.some((row) => row.item.updatedDate);

  return (
    <>
      <div className={styles.head}>
        <FilesViewToggle
          className={browserStyles.viewToggle}
          view={view}
          onChange={(next) => {
            onBi?.(`files_view_${next}`);
            setView(next);
          }}
        />
      </div>
      {rows.length === 0 ? (
        <div className={browserStyles.noResults}>{emptyCaption}</div>
      ) : view === 'thumbnails' ? (
        <FilesGrid
          rows={rows}
          folders={[]}
          onOpenFolder={() => {}}
          onOpen={onOpen}
          onShare={onShare}
          onRowMenu={onRowMenu}
          actionsFor={actionsFor}
        />
      ) : (
        <FilesTable
          rows={rows}
          folders={[]}
          onOpenFolder={() => {}}
          sort={sort}
          onSortChange={(next) => {
            onBi?.(`files_sort_${next.key}`);
            setSort(next);
          }}
          showCreatedBy={showCreatedBy}
          showDate={showDate}
          onOpen={onOpen}
          onShare={onShare}
          onRowMenu={onRowMenu}
          actionsFor={actionsFor}
        />
      )}
    </>
  );
}
